import { ThemeSwitcher } from '../theme-switcher'
import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { SIGN_OUT_FAILURE, signOutAndRedirect } from '../auth/sign-out'
import { useAdminAuth } from '../auth/use-admin-auth'
import { AdminChangePasswordDialog } from '../auth/change-password'
import {
  requestDiscardUnsavedChanges,
  resetDocumentExitAuthorizationAfterAttempt,
} from '../common/unsaved-changes'
import {
  ADMIN_FOOTER_ITEMS,
  ADMIN_NAVIGATION_GROUPS,
  isNavigationItemActive,
} from '../navigation/admin-navigation'
import { useOperatorFaq } from '../operator-faq/operator-faq'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AppSidebar,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Icon,
  SidebarTrigger,
  useSidebar,
} from '@speaking-coach/ui'

/**
 * The top bar's trigger, only on a narrow window, where it opens the
 * navigation sheet; otherwise the sidebar's own toggle collapses it.
 */
export function AdminSidebarTrigger(): JSX.Element | null {
  const { isNarrow } = useSidebar()
  if (!isNarrow) return null
  return (
    <>
      <SidebarTrigger label="Open navigation" />
      <div aria-hidden="true" className="h-6 w-px bg-border-default" />
    </>
  )
}

/** Admin's navigation inventory and operator menu, in the shared sidebar. */
export function AdminSidebar(): JSX.Element {
  const { pathname } = useLocation()
  const { isNarrow, open } = useSidebar()
  const { user, logout } = useAdminAuth()
  const { openFaq } = useOperatorFaq()
  const [signingOut, setSigningOut] = useState(false)
  const [signOutFailed, setSignOutFailed] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const operatorName = user?.name.trim() || user?.email || 'Operator'

  return (
    <>
      <AppSidebar
        label="Admin navigation"
        description="Navigate between Cooori administration tools."
        toggleLabel={
          isNarrow ? 'Close navigation' : open ? 'Collapse navigation' : 'Expand navigation'
        }
        groups={ADMIN_NAVIGATION_GROUPS.map((group) => ({
          id: group.id,
          label: group.label,
          items: group.items.map((item) => ({
            id: item.id,
            label: item.label,
            icon: item.icon,
            link: <Link to={item.to} />,
            active: isNavigationItemActive(pathname, item),
          })),
        }))}
        account={{
          name: operatorName,
          detail: user?.email || 'Cooori admin',
          label: `Open operator menu for ${operatorName}`,
          menu: (
            <>
              <div role="none" className="px-2 py-1"><ThemeSwitcher showLabel /></div>
              <DropdownMenuSeparator />
              {ADMIN_FOOTER_ITEMS.map((item) => (
                <DropdownMenuItem key={item.id} onSelect={() => openFaq()}>
                  <Icon name={item.icon} />
                  <span>{item.label}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setChangingPassword(true)}>
                <Icon name="changePassword" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={signingOut}
                onSelect={() => {
                  setSigningOut(true)
                  void signOutAndRedirect(
                    logout,
                    undefined,
                    undefined,
                    requestDiscardUnsavedChanges,
                  ).catch(() => setSignOutFailed(true)).finally(() => {
                    resetDocumentExitAuthorizationAfterAttempt()
                    setSigningOut(false)
                  })
                }}
              >
                <Icon name="signOut" />
                {signingOut ? 'Signing out…' : 'Sign out'}
              </DropdownMenuItem>
            </>
          ),
        }}
      />
      <AdminChangePasswordDialog open={changingPassword} onOpenChange={setChangingPassword} />
      <AlertDialog open={signOutFailed} onOpenChange={setSignOutFailed}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Could not sign out</AlertDialogTitle>
            <AlertDialogDescription>{SIGN_OUT_FAILURE}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
